import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useQuery } from "react-query";
import { useForm, Controller } from "react-hook-form";
import { handleFileUpload } from "Api/Pages/cloudinaryAssetUpload";
import { setToastMessage } from "App/commonReducer";
import { useDispatch } from "react-redux";
import { getCoachList } from "Api/Pages/CoachesApi";
import USE_QUERY_KEYS_CONSTANTS from "Api/useQueryKeyConstants";
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CoachPromotionalOfferFormData } from "./types";

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Col,
  Form,
  Row,
} from "Components/Core";
import AssetTags from "Components/Shared/AssetTags";

import {
  assetTagsReducer,
  defaultAssetTagsData,
} from "Components/Shared/AssetTags/utils";

import {
  ASSET_TYPE_ENUM,
  IAssetDetails,

  // SizeConfigType,
  UPLOAD_PURPOSE_ENUM,
} from "./types";

import "./styles.scss";
import { AssetTagsData } from "Interfaces/AssetTagsInterface";

interface IProps {
  isModalVisible: boolean;

  title: string;
  assetType?: ASSET_TYPE_ENUM;
  //sizeConfig: SizeConfigType;

  purpose: UPLOAD_PURPOSE_ENUM;
  sportsList?: Array<{
    id: number;
    image_link: string;
    name: string;
  }>;
  toggleModal: VoidFunction;
  onAssetSubmitCallback: (
    videoDetails: IAssetDetails,
    assetTagsData: AssetTagsData
  ) => void;
}

const UploadAsset = (props: IProps) => {
  const {
    isModalVisible,
    title = "Upload Asset",
    purpose,
    toggleModal,
    onAssetSubmitCallback,
  } = props;

  const {
    formState: { errors },
    control,
    handleSubmit,
    watch,
    setValue,
  } = useForm<IAssetDetails>({
    defaultValues: {
      thumbnail_url: "",
      kpoint_gcc_id: "",
      description: "",
      title: "",
      asset: null,
      sport: "",
    },
  });
  const dispatch = useDispatch();
  const initialAssetTagsData = useMemo(() => defaultAssetTagsData(), []);
  const [assetTagsData, assetTagsDispatch] = useReducer(
    assetTagsReducer,
    initialAssetTagsData
  );
  const [assetTagsError, setAssetTagsError] = useState<string | null>(null);

  // const [uploadError, setUploadError] = useState<string | null>(null);
  const [objectKey, setObjecyKey] = useState<string | null>(null);

  const isPromotional = purpose === UPLOAD_PURPOSE_ENUM.public;

  const [selectedCoachId, setSelectedCoachId] = useState<number | "">("");
  const [offers, setOffers] = useState<CoachPromotionalOfferFormData[]>([]);

  const { data: coachesData } = useQuery(
    USE_QUERY_KEYS_CONSTANTS.coachesList,
    () =>
      getCoachList({
        academyId: 0,
        searchInput: "",
        selectValue: "",
        currentPage: 1,
      }),
    { enabled: isPromotional && isModalVisible, refetchOnWindowFocus: false }
  );

  const coaches = coachesData?.coaches || [];
  const selectedCoach = coaches.find(
    (c: { id: number }) => c.id === selectedCoachId
  );

  const addOffer = () => {
    if (offers.length > 0 || !selectedCoachId) return;
    setOffers((prev) => [
      ...prev,
      {
        coach_id: selectedCoachId as number,
        video_count: 3,
        review_rates: { INR: 0, USD: 0 },
      },
    ]);
  };

  const removeOffer = (index: number) => {
    setOffers((prev) => prev.filter((_, i) => i !== index));
  };

  const updateOfferRate = (
    index: number,
    currency: "INR" | "USD",
    value: string
  ) => {
    setOffers((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        review_rates: {
          ...updated[index].review_rates,
          [currency]: Number(value) || 0,
        },
      };
      return updated;
    });
  };

  const handleCoachChange = (id: number | "") => {
    setSelectedCoachId(id);
    setOffers([]);
  };

  const validAssetTagsData = (): string | null => {
    if (
      purpose === UPLOAD_PURPOSE_ENUM.drill &&
      !assetTagsData.master_sport_id
    ) {
      return "Sport is required";
    }

    return null;
  };
  const uploadType = watch("of_type", ASSET_TYPE_ENUM.image);
  useEffect(() => {
    setValue("of_type", ASSET_TYPE_ENUM.image);
  }, [setValue]);
  const onSubmit = (data: any) => {
    if (purpose === UPLOAD_PURPOSE_ENUM.public) {
      const validOffers = offers.filter(
        (o) => o.review_rates.INR > 0 && o.review_rates.USD > 0
      );
      onAssetSubmitCallback(
        {
          ...data,
          purpose,
          object_key: objectKey,
          promotional_offers: validOffers.length ? validOffers : undefined,
        },
        assetTagsData
      );
      return toggleModal();
    }

    const assetError = validAssetTagsData();
    setAssetTagsError(assetError);

    if (!assetError) {
      const validOffers = offers.filter(
        (o) => o.review_rates.INR > 0 && o.review_rates.USD > 0
      );
      onAssetSubmitCallback(
        {
          ...data,
          purpose,
          promotional_offers: validOffers.length ? validOffers : undefined,
        },
        assetTagsData
      );
      toggleModal();
    }
  };

  const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    if (purpose !== UPLOAD_PURPOSE_ENUM.public) {
      setAssetTagsError(validAssetTagsData());
    }

    handleSubmit(onSubmit)(e);
  };

  const assetTagsDispatchWrapper = useCallback(
    (action: { type: string; payload: any }) => {
      setAssetTagsError(null);
      assetTagsDispatch(action);
    },
    []
  );

  return (
    <Modal centered size="lg" show={isModalVisible}>
      <ModalHeader>{title}</ModalHeader>

      <Form className="d-flex flex-column scroll-y" onSubmit={handleFormSubmit}>
        <ModalBody style={{ overflowY: "auto", maxHeight: "70vh" }}>
          <Row>
            <Col sm={8}>
              <Form.Group controlId="textarea">
                <Form.Label className="fs-xs">Title*</Form.Label>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: "Title is required" }}
                  render={({ field }) => (
                    <Form.Control
                      type="text"
                      className="border"
                      placeholder="Start typing title here"
                      {...field}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.title?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col sm={8}>
              <Form.Group controlId="textarea">
                <Form.Label className="fs-xs">Description*</Form.Label>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: "Description is required" }}
                  render={({ field }) => (
                    <Form.Control
                      as="textarea"
                      rows={5}
                      className="border"
                      placeholder="Tell us more about the video"
                      {...field}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.description?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Conditional Section for Public Purpose */}
          {purpose === UPLOAD_PURPOSE_ENUM.public && (
            <>
              <Row>
                <Col sm={8}>
                  <Form.Group controlId="uploadType">
                    <Form.Label>Select Upload Type</Form.Label>
                    <Controller
                      name="of_type"
                      control={control}
                      defaultValue={uploadType}
                      render={({ field }) => (
                        <Form.Control as="select" {...field}>
                          <option value="image">Upload Image</option>
                          <option value="video">Upload Video</option>
                        </Form.Control>
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Conditionally render fields based on upload type */}
              {watch("of_type") === "image" && (
                <Row>
                  <Col sm={8}>
                    <Form.Group controlId="image">
                      <Form.Label>Upload Image</Form.Label>
                      <Controller
                        name="image"
                        control={control}
                        rules={{ required: "Image is required" }}
                        render={({ field }) => (
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={async (
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const file = e.target.files
                                ? e.target.files[0]
                                : null;

                              if (file) {
                                if (!file.type.startsWith("image/")) {
                                  dispatch(
                                    setToastMessage([
                                      ["Please Select Valid Image Format"],
                                    ])
                                  );

                                  e.target.value = "";
                                  return;
                                }
                                field.onChange(file);

                                const objectKey = await handleFileUpload(file);
                                setObjecyKey(objectKey);
                              }
                            }}
                          />
                        )}
                      />
                      {errors.image && (
                        <Form.Text className="text-danger">
                          {errors.image.message}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {watch("of_type") === "video" && (
                <>
                  <Row>
                    <Col sm={8}>
                      <Form.Group controlId="kpoint_gcc_id">
                        <Form.Label>GCC ID</Form.Label>
                        <Controller
                          name="kpoint_gcc_id"
                          control={control}
                          rules={{ required: "GCC ID is required" }}
                          render={({ field }) => (
                            <Form.Control
                              type="text"
                              className="border"
                              placeholder="Enter GCC ID"
                              {...field}
                            />
                          )}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={8}>
                      <Form.Group controlId="thumbnail_url">
                        <Form.Label>Thumbnail URL</Form.Label>
                        <Controller
                          name="thumbnail_url"
                          control={control}
                          rules={{ required: "Thumbnail URL is required" }}
                          render={({ field }) => (
                            <Form.Control
                              type="text"
                              className="border"
                              placeholder="Enter Thumbnail URL"
                              {...field}
                            />
                          )}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
            </>
          )}

          {/* Fields and Tags for Non-Public Purposes */}
          {purpose !== UPLOAD_PURPOSE_ENUM.public && (
            <>
              <Row>
                <Col sm={8}>
                  <Form.Group controlId="kpoint_gcc_id">
                    <Form.Label>GCC ID</Form.Label>
                    <Controller
                      name="kpoint_gcc_id"
                      control={control}
                      rules={{ required: "GCC ID is required" }}
                      render={({ field }) => (
                        <Form.Control
                          type="text"
                          className="border"
                          placeholder="Enter GCC ID"
                          {...field}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col sm={8}>
                  <Form.Group controlId="thumbnail_url">
                    <Form.Label>Thumbnail URL</Form.Label>
                    <Controller
                      name="thumbnail_url"
                      control={control}
                      rules={{ required: "Thumbnail URL is required" }}
                      render={({ field }) => (
                        <Form.Control
                          type="text"
                          className="border"
                          placeholder="Enter Thumbnail URL"
                          {...field}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col sm={8}>
                  <AssetTags
                    formData={assetTagsData}
                    dispatch={assetTagsDispatchWrapper}
                    disabled={false}
                  />
                </Col>
              </Row>
              {assetTagsError && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {assetTagsError}
                </Form.Control.Feedback>
              )}
            </>
          )}

          {/* Coach Promotional Offers — promotional assets only */}
          {isPromotional && (
            <>
              <hr className="my-3" />
              <Row>
                <Col sm={8}>
                  <Form.Group controlId="promoCoach">
                    <Form.Label className="fs-xs fw-semibold">
                      Coach Promotion (Optional)
                    </Form.Label>
                    <Form.Select
                      className="ss-select-control bg-white"
                      value={selectedCoachId}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleCoachChange(Number(e.target.value) || "")
                      }
                    >
                      <option value="">Select Coach</option>
                      {coaches.map((c: { id: number; name: string }) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {selectedCoach && (
                <Row className="mt-1">
                  <Col sm={8}>
                    <small className="text-muted fs-xs">
                      Per-video rate — ₹{selectedCoach.video_review_rates?.INR}{" "}
                      / ${selectedCoach.video_review_rates?.USD}
                    </small>
                  </Col>
                </Row>
              )}

              {offers.map((offer, index) => (
                <div
                  key={index}
                  className="sports-details-wrap p-3 mt-2 position-relative"
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="fs-6 text-secondary position-absolute"
                    style={{
                      cursor: "pointer",
                      top: "0.75rem",
                      right: "0.75rem",
                    }}
                    onClick={() => removeOffer(index)}
                  />
                  <Row className="g-2 align-items-end">
                    <Col sm={4}>
                      <Form.Label className="fs-xs">Bundle</Form.Label>
                      <Form.Control
                        type="text"
                        className="bg-light"
                        value={`${offer.video_count} Videos`}
                        disabled
                      />
                    </Col>
                    <Col sm={4}>
                      <Form.Label className="fs-xs">Rate (INR) *</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        className="bg-white"
                        placeholder="e.g. 2500"
                        value={offer.review_rates.INR || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateOfferRate(index, "INR", e.target.value)
                        }
                      />
                    </Col>
                    <Col sm={4}>
                      <Form.Label className="fs-xs">Rate (USD) *</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        step={0.01}
                        className="bg-white"
                        placeholder="e.g. 35"
                        value={offer.review_rates.USD || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateOfferRate(index, "USD", e.target.value)
                        }
                      />
                    </Col>
                  </Row>
                </div>
              ))}

              {selectedCoachId && offers.length < 1 && (
                <Row className="mt-2">
                  <Col sm={8}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      type="button"
                      onClick={addOffer}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      Add Bundle Offer
                    </Button>
                  </Col>
                </Row>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline-secondary"
            size="sm"
            className="px-4"
            onClick={toggleModal}
          >
            Cancel
          </Button>
          <Button variant="secondary" size="sm" className="px-4" type="submit">
            Save
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default UploadAsset;
